import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GET_PROJECT_VISIBILITY_CHART } from '../../../query/project/stats/keys';
import { ChatType } from '../../../../types/monitor/ChatType';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import { ProjectVisibilityPoint } from '../../../../models/projects/stats/AllBrandsStatsResponse';
import { getProjectVisibilityChart } from '../../../services/project/stats/ProjectDailyStatsService';

/**
 * Time-series of the Visibility % metric (responses mentioning the brand / total
 * responses) bucketed by the active period. Date keys align with the cumulative
 * daily stats so this can be plotted alongside the average-position series on the
 * Brand Position chart (V2 preview page).
 */
export const useGetProjectVisibilityChart = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  period: MonitorPeriod | null,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && !!period && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const {
    data: visibilityChart,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ProjectVisibilityPoint[]>({
    queryKey: [GET_PROJECT_VISIBILITY_CHART, projectId, chatType, period, from, to, monitorPromptIds, filterChatTypes],
    queryFn: () => {
      return getProjectVisibilityChart(projectId!, chatType!, period!, from, to, filterChatTypes, monitorPromptIds);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    visibilityChart: queryEnabled ? visibilityChart ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () => {
      return queryClient.invalidateQueries({
        queryKey: [GET_PROJECT_VISIBILITY_CHART, projectId, chatType, period, from, to, monitorPromptIds, filterChatTypes],
        refetchType: 'active',
      });
    },
  };
};
